import { useState } from "react";
import { SearchField } from "@/components/ui/search-field";
import TrainerModuleNode from "@/components/course/tree/TrainerModuleNode";
import { treeChromeClass } from "@/components/course/tree/treeStyles";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { cn } from "@/lib/utils";

interface FilteredCourseModules {
  modules: CourseTreeNode[];
  expandedModuleIds: Set<string> | null;
}

const chaptersOf = (module: CourseTreeNode) =>
  (module.children ?? []).filter(
    (child) => child.type === CourseTreeNodeTypeEnum.Chapter
  );

const filterCourseModules = (
  modules: CourseTreeNode[],
  searchQuery: string
): FilteredCourseModules => {
  const query = searchQuery.trim().toLowerCase();
  if (!query) {
    return { modules, expandedModuleIds: null };
  }

  const expandedModuleIds = new Set<string>();
  const matchingModules = modules.flatMap((module) => {
    const moduleMatches = module.title.toLowerCase().includes(query);
    const chapters = chaptersOf(module);
    const matchedChapters = chapters.filter((chapter) =>
      chapter.title.toLowerCase().includes(query)
    );
    const visibleChapters = moduleMatches ? chapters : matchedChapters;
    if (!moduleMatches && matchedChapters.length === 0) return [];

    expandedModuleIds.add(module.id);
    return [{ ...module, children: visibleChapters }];
  });

  return { modules: matchingModules, expandedModuleIds };
};

const renderFilteredModules = (
  filtered: FilteredCourseModules,
  isSearching: boolean,
  expandedModules: Set<string>,
  onToggle: (moduleId: string) => void
) => {
  if (filtered.modules.length === 0 && isSearching) {
    return (
      <p className={cn("px-5 py-4 text-sm", treeChromeClass)}>Keine Treffer</p>
    );
  }

  return filtered.modules.map((module, moduleIndex) => {
    let isExpanded = expandedModules.has(module.id);
    if (isSearching) {
      isExpanded = Boolean(filtered.expandedModuleIds?.has(module.id));
    }

    return (
      <TrainerModuleNode
        key={module.id}
        module={module}
        isFirst={moduleIndex === 0}
        isExpanded={isExpanded}
        onToggle={onToggle}
      />
    );
  });
};

/**
 * Module and chapter list a trainer uses to open any place in the course.
 */
const TrainerCourseTree = () => {
  const courseData = useCourseDetailStore((state) => state.courseData);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    () => new Set()
  );

  const modules =
    courseData?.structure.filter(
      (node) => node.type === CourseTreeNodeTypeEnum.Module
    ) ?? [];
  const filtered = filterCourseModules(modules, searchQuery);
  const isSearching = searchQuery.trim().length > 0;

  const toggleModule = (moduleId: string) => {
    setExpandedModules((previous) => {
      const next = new Set(previous);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-5 py-4">
        <SearchField
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Kapitel suchen…"
        />
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        {renderFilteredModules(
          filtered,
          isSearching,
          expandedModules,
          toggleModule
        )}
      </div>
    </div>
  );
};

export default TrainerCourseTree;
